import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';

/**
 * useSectionCollapse
 *
 * A drop-in replacement for `useState(false/true)` on any collapsible section.
 * The user's last-chosen state is persisted to `localStorage` under a unique key,
 * so preferences survive page reloads and in-app navigation without any backend round-trip.
 *
 * Usage — any section can opt into this with a single line:
 *
 *   const [isCollapsed, toggleCollapsed] = useSectionCollapse('attachments', true);
 *   const [isCollapsed, toggleCollapsed] = useSectionCollapse('tenant-details', false);
 *
 * The storage key is namespaced as `captrac:section-collapse:{sectionKey}` to avoid
 * collisions with other localStorage entries.  If localStorage is unavailable (private
 * browsing, quota exceeded, etc.) the hook degrades silently to in-memory state and
 * captures the error to Sentry.
 *
 * @param {string}  sectionKey       - Unique identifier for this section (e.g. 'attachments').
 *                                     Should be kebab-case and stable across deploys.
 * @param {boolean} [defaultCollapsed=false] - The out-of-box default when the user has never
 *                                     interacted with this section before.  Pass `true` to
 *                                     start a section collapsed (e.g. Attachments), or `false`
 *                                     (the hook default) to start it expanded.  Either way, once
 *                                     the user toggles the section their choice is stored and this
 *                                     param has no further effect.
 * @returns {[boolean, () => void]}  - [isCollapsed, toggleCollapsed]
 */
const STORAGE_PREFIX = 'captrac:section-collapse:';

const useSectionCollapse = (sectionKey, defaultCollapsed = false) => {
    const storageKey = `${STORAGE_PREFIX}${sectionKey}`;

    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            // Only use the stored value if it was explicitly saved; otherwise use the
            // caller-supplied default so new sections respect their intended initial state.
            if (stored !== null) {
                return JSON.parse(stored);
            }
        } catch (initError) {
            Sentry.captureException(initError);
            // Corrupted entry or unavailable storage — fall through to the default
        }
        return defaultCollapsed;
    });

    /**
     * Toggles the collapsed state and persists the new value to localStorage so
     * the user's choice is remembered the next time they open this page.
     */
    const toggleCollapsed = useCallback(() => {
        setIsCollapsed(prev => {
            const nextValue = !prev;
            try {
                localStorage.setItem(storageKey, JSON.stringify(nextValue));
            } catch (saveError) {
                Sentry.captureException(saveError);
                // Storage full or unavailable — the in-memory toggle still works for the
                // remainder of this session, but the preference won't survive a page reload.
            }
            return nextValue;
        });
    }, [storageKey]);

    return [isCollapsed, toggleCollapsed];
};

export default useSectionCollapse;
